/**
 * Date relative d'un article (« aujourd'hui », « il y a 3 jours »).
 *
 * LA DATE N'A PAS D'HEURE. L'API renvoie « YYYY-MM-DD » et rien d'autre. La tentation est de faire
 * `new Date('2026-08-20')` puis de soustraire : mais cette forme est interprétée comme MINUIT UTC,
 * si bien qu'à Paris en été l'article paraît publié à 2 h du matin, et qu'à Los Angeles il paraît
 * publié la VEILLE à 17 h. Selon le fuseau, le même article affiche alors « aujourd'hui » ou
 * « il y a 1 jour » — un décalage invisible en développement, systématique pour une partie des
 * utilisateurs.
 *
 * On ne compare donc pas des instants mais des QUANTIÈMES : les trois nombres de l'article d'un
 * côté, ceux du jour local de l'autre, tous deux projetés sur midi UTC. Aucune heure n'est
 * supposée, aucun fuseau n'intervient, et l'écart en jours est exact.
 *
 * Renvoie `null` si la date est absente ou malformée : l'appelant n'affiche alors simplement rien,
 * plutôt qu'un « il y a NaN jours ».
 */
const JOUR_MS = 24 * 60 * 60 * 1000;

/** Projette un quantième sur midi UTC : à l'abri d'un décalage d'heure d'été de part et d'autre. */
function midiUTC(annee: number, mois: number, jour: number): number {
  return Date.UTC(annee, mois, jour, 12, 0, 0, 0);
}

/**
 * Écart en JOURS PLEINS entre la date d'un article et aujourd'hui. Négatif si la date est à venir —
 * un article programmé ne doit pas s'afficher « il y a -2 jours ».
 */
export function daysSince(date: unknown, now: Date = new Date()): number | null {
  if (typeof date !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());
  if (!m) return null;

  const annee = Number(m[1]);
  const mois = Number(m[2]) - 1;
  const jour = Number(m[3]);
  const article = midiUTC(annee, mois, jour);

  // Contrôle de VALIDITÉ, pas de forme : « 2026-02-31 » a la bonne forme et n'existe pas. Date.UTC
  // la reporte silencieusement au 3 mars ; on la refuse plutôt que d'afficher une date fantôme.
  const relu = new Date(article);
  if (relu.getUTCFullYear() !== annee || relu.getUTCMonth() !== mois || relu.getUTCDate() !== jour) {
    return null;
  }

  const aujourdhui = midiUTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((aujourdhui - article) / JOUR_MS);
}
